import { propertiesApi, type PropertyGalleryPhotoDto } from '../../services/api';
import { ApiError, authorizedFetch } from '../../services/core.api';
import { t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { clearModalPortal, getModalPortalRoot, renderModalPortal } from '../shared/modal-portal';
import { PROPERTY_ICONS, escapeHtml, formatDate } from './property-shared';

export class RcPropertyGalleryTab extends HTMLElement {
  propertyId = '';
  photos: PropertyGalleryPhotoDto[] = [];
  private uploadError = '';
  private isUploading = false;
  private photoObjectUrls = new Map<string, string>();
  private activePhotoId: string | null = null;

  connectedCallback(): void {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    getModalPortalRoot(this).addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.render();
    void this.ensurePhotoUrls();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    getModalPortalRoot(this).removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
    clearModalPortal(this);
    this.revokePhotoUrls();
  }

  render(): void {
    this.syncPhotoUrlCache();

    const activePhoto = this.photos.find((photo) => photo.id === this.activePhotoId) ?? null;
    const activePhotoIndex = activePhoto ? this.photos.findIndex((photo) => photo.id === activePhoto.id) : -1;
    const canNavigatePrevious = activePhotoIndex > 0;
    const canNavigateNext = activePhotoIndex >= 0 && activePhotoIndex < this.photos.length - 1;

    this.innerHTML = `
      <div class="tab-toolbar" style="align-items:flex-start;">
        <div style="display:flex;flex-direction:column;gap:0.35rem;">
          <strong>${t('property.gallery.title')}</strong>
          <span class="text-muted text-sm">${t('property.gallery.helper')}</span>
        </div>
        <label class="btn btn-primary btn-sm ${this.isUploading ? 'disabled' : ''}" style="${this.isUploading ? 'pointer-events:none;opacity:0.7;' : ''}">
          ${PROPERTY_ICONS.plus} ${this.isUploading ? t('common.uploading') : t('property.gallery.upload')}
          <input type="file" accept="image/*" multiple hidden ${this.isUploading ? 'disabled' : ''}>
        </label>
      </div>
      ${this.uploadError ? `<div class="form-error">${escapeHtml(this.uploadError)}</div>` : ''}
      ${this.photos.length === 0
        ? `<div class="empty-state"><p>${t('property.gallery.empty')}</p></div>`
        : `<div class="property-gallery-grid">
            ${this.photos.map((photo) => `
              <article class="card property-gallery-card">
                <button
                  type="button"
                  class="property-gallery-thumb"
                  data-action="open-lightbox"
                  data-id="${photo.id}"
                  aria-label="${escapeHtml(t('property.gallery.openPreview', { fileName: photo.fileName }))}">
                  <img src="${escapeHtml(this.photoObjectUrls.get(photo.id) || '')}" alt="${escapeHtml(t('property.gallery.previewAlt', { fileName: photo.fileName }))}">
                </button>
                <div class="property-gallery-card-body">
                  <div>
                    <div class="doc-filename">${escapeHtml(photo.fileName)}</div>
                    <div class="text-muted text-sm">${formatDate(photo.createdAt)}</div>
                  </div>
                  <div class="card-actions">
                    <button class="btn btn-outline btn-sm" data-action="download" data-id="${photo.id}">${t('common.download')}</button>
                    <button class="btn btn-danger btn-sm" data-action="delete" data-id="${photo.id}">${t('common.delete')}</button>
                  </div>
                </div>
              </article>
            `).join('')}
          </div>`}
    `;

    renderModalPortal(this, activePhoto ? `
      <div class="modal-overlay property-gallery-lightbox" data-action="close-lightbox">
        <div class="modal property-gallery-lightbox-modal" role="dialog" aria-modal="true" aria-label="${escapeHtml(t('property.gallery.lightboxTitle'))}">
          <div class="property-gallery-lightbox-header">
            <div class="property-gallery-lightbox-copy">
              <strong>${escapeHtml(activePhoto.fileName)}</strong>
              <span class="text-muted text-sm">${escapeHtml(t('property.gallery.position', { current: String(activePhotoIndex + 1), total: String(this.photos.length) }))}</span>
            </div>
            <button type="button" class="modal-close" data-action="close-lightbox" aria-label="${escapeHtml(t('property.gallery.closePreview'))}">
              ${PROPERTY_ICONS.close}
            </button>
          </div>
          <div class="property-gallery-lightbox-stage">
            <button
              type="button"
              class="property-gallery-lightbox-nav"
              data-action="previous-photo"
              ${canNavigatePrevious ? '' : 'disabled'}
              aria-label="${escapeHtml(t('property.gallery.previous'))}">
              &#8249;
            </button>
            <figure class="property-gallery-lightbox-figure">
              <img src="${escapeHtml(this.photoObjectUrls.get(activePhoto.id) || '')}" alt="${escapeHtml(t('property.gallery.previewAlt', { fileName: activePhoto.fileName }))}">
            </figure>
            <button
              type="button"
              class="property-gallery-lightbox-nav"
              data-action="next-photo"
              ${canNavigateNext ? '' : 'disabled'}
              aria-label="${escapeHtml(t('property.gallery.next'))}">
              &#8250;
            </button>
          </div>
        </div>
      </div>
    ` : '');

    void this.ensurePhotoUrls();
  }

  private handleClick = async (event: Event): Promise<void> => {
    const rawTarget = event.target as HTMLElement | null;
    const target = rawTarget?.closest<HTMLElement>('[data-action]');
    if (!target) {
      return;
    }

    if (target.dataset.action === 'open-lightbox' && target.dataset.id) {
      this.activePhotoId = target.dataset.id;
      this.render();
      return;
    }

    if (target.dataset.action === 'close-lightbox') {
      if (target === rawTarget || target.classList.contains('modal-close')) {
        this.closeLightbox();
      }
      return;
    }

    if (target.dataset.action === 'previous-photo') {
      this.showRelativePhoto(-1);
      return;
    }

    if (target.dataset.action === 'next-photo') {
      this.showRelativePhoto(1);
      return;
    }

    if (target.dataset.action === 'download' && target.dataset.id) {
      await this.downloadPhoto(target.dataset.id);
      return;
    }

    if (target.dataset.action === 'delete' && target.dataset.id) {
      if (!await confirmDialog(t('property.gallery.confirmDelete'))) {
        return;
      }

      try {
        await propertiesApi.deleteGalleryPhoto(target.dataset.id);
        this.dispatchEvent(new CustomEvent('property-gallery-changed', { bubbles: true }));
      } catch (error) {
        console.error('Failed to delete property gallery photo:', error);
        toastService.error(error instanceof Error ? error.message : t('common.errorGeneric'));
      }
    }
  };

  private async ensurePhotoUrls(): Promise<void> {
    const missingPhotos = this.photos.filter((photo) => !this.photoObjectUrls.has(photo.id));
    if (!missingPhotos.length) {
      return;
    }

    try {
      await Promise.all(missingPhotos.map(async (photo) => {
        const response = await authorizedFetch(photo.downloadUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        this.photoObjectUrls.set(photo.id, URL.createObjectURL(blob));
      }));

      if (this.isConnected) {
        this.render();
      }
    } catch (error) {
      console.error('Failed to load property gallery photos:', error);
      toastService.error(error instanceof Error ? error.message : t('common.errorGeneric'));
    }
  }

  private revokePhotoUrls(): void {
    this.photoObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.photoObjectUrls.clear();
  }

  private syncPhotoUrlCache(): void {
    const activePhotoIds = new Set(this.photos.map((photo) => photo.id));
    for (const [photoId, url] of this.photoObjectUrls.entries()) {
      if (activePhotoIds.has(photoId)) {
        continue;
      }

      URL.revokeObjectURL(url);
      this.photoObjectUrls.delete(photoId);
    }

    if (this.activePhotoId && !activePhotoIds.has(this.activePhotoId)) {
      this.activePhotoId = null;
    }
  }

  private async downloadPhoto(photoId: string): Promise<void> {
    const photo = this.photos.find((item) => item.id === photoId);
    if (!photo) {
      return;
    }

    try {
      const response = await authorizedFetch(photo.downloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = photo.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to download property gallery photo:', error);
      toastService.error(error instanceof Error ? error.message : t('common.errorGeneric'));
    }
  }

  private handleChange = async (event: Event): Promise<void> => {
    const input = event.target as HTMLInputElement | null;
    if (!input || input.type !== 'file' || !input.files?.length || !this.propertyId) {
      return;
    }

    this.isUploading = true;
    this.uploadError = '';
    this.render();

    try {
      for (const file of Array.from(input.files)) {
        const upload = await propertiesApi.createGalleryPhotoUpload(this.propertyId, {
          fileName: file.name,
          contentType: file.type || 'image/jpeg',
          fileSizeBytes: file.size,
        });
        const response = await fetch(upload.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'image/jpeg' },
          body: file,
        });
        if (!response.ok) {
          await propertiesApi.deleteGalleryPhoto(upload.photoId).catch(() => undefined);
          throw new Error(t('document.uploadError'));
        }
      }

      this.dispatchEvent(new CustomEvent('property-gallery-changed', { bubbles: true }));
    } catch (error) {
      console.error('Failed to upload property gallery photos:', error);
      this.uploadError = error instanceof ApiError ? error.message : (error instanceof Error ? error.message : t('common.errorGeneric'));
      toastService.error(this.uploadError);
    } finally {
      input.value = '';
      this.isUploading = false;
      this.render();
    }
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    if (!this.activePhotoId) {
      return;
    }

    if (event.key === 'Escape') {
      this.closeLightbox();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showRelativePhoto(-1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showRelativePhoto(1);
    }
  };

  private closeLightbox(): void {
    if (!this.activePhotoId) {
      return;
    }

    this.activePhotoId = null;
    this.render();
  }

  private showRelativePhoto(offset: number): void {
    if (!this.activePhotoId) {
      return;
    }

    const currentIndex = this.photos.findIndex((photo) => photo.id === this.activePhotoId);
    if (currentIndex < 0) {
      this.closeLightbox();
      return;
    }

    const nextIndex = currentIndex + offset;
    if (nextIndex < 0 || nextIndex >= this.photos.length) {
      return;
    }

    this.activePhotoId = this.photos[nextIndex].id;
    this.render();
  }
}

if (!customElements.get('rc-property-gallery-tab')) {
  customElements.define('rc-property-gallery-tab', RcPropertyGalleryTab);
}
